/**
 * i18next bootstrap — imported first thing in `main.tsx`, before React renders. Catalogs are
 * bundled (`./resources`), so i18next initialises synchronously and the first paint is already translated.
 * Language = stored choice (`hp-lang`) → first browser language we support → English. The
 * active language is mirrored to `<html lang>` so browsers stop offering to machine-translate a
 * page that already is in the reader's language.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGUAGES, NAMESPACES, resources, type Language } from './resources';

const STORAGE_KEY = 'hp-lang';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

function storedLanguage(): Language | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

/** Stored choice, else the first browser language we support, else English. */
export function detectLanguage(): Language {
  const stored = storedLanguage();
  if (stored) return stored;
  const candidates = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLanguage(base)) return base;
  }
  return 'en';
}

/** The language the toggle switches TO (two languages, so the other one). */
export function otherLanguage(current: string): Language {
  return current === 'cs' ? 'en' : 'cs';
}

export async function setLanguage(language: Language): Promise<void> {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // the preference is a convenience only
  }
  await i18next.changeLanguage(language);
}

i18next.on('languageChanged', (language: string) => {
  document.documentElement.lang = language;
});

void i18next.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...LANGUAGES],
  ns: NAMESPACES,
  defaultNS: 'common',
  // React escapes for us; keeping this on would turn "Apollo & Lusha" into "&amp;".
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { useSuspense: false },
});

export default i18next;
